/**
 * BlogProductCards — product cards a writer placed in an article by putting
 * product links on their own line (see articleBody.ts). Falls back to the
 * original links while loading or if a product isn't found.
 */
import { Link } from "@tanstack/react-router";
import { Image, Money } from "@shopify/hydrogen-react";
import { useTranslation } from "react-i18next";
import { cn } from "@ui/lib/utils";
import {
  CardArrow,
  cardMedia,
  cardSurface,
} from "@modules/shared/Card/cardStyles";
import {
  BasePriceLine,
  cheapestVariantBasePrice,
} from "@modules/shared/Product/BasePrice";
import type { GetProductByHandleQuery } from "@gql/types";

type BlogProduct = NonNullable<GetProductByHandleQuery["product"]>;

interface BlogProductCardsProps {
  handles: Array<string>;
  products: Array<BlogProduct>;
  fallbackHtml: string;
  locale: string;
}

export function BlogProductCards({
  handles,
  products,
  fallbackHtml,
  locale,
}: Readonly<BlogProductCardsProps>) {
  const { t } = useTranslation();
  const shown = handles
    .map((handle) => products.find((p) => p.handle === handle))
    .filter((p) => p !== undefined);

  if (shown.length === 0) {
    return (
      <div
        className="blog-article-content"
        dangerouslySetInnerHTML={{ __html: fallbackHtml }}
      />
    );
  }

  return (
    <div
      className={cn("my-10 grid gap-4", shown.length > 1 && "sm:grid-cols-2")}
    >
      {shown.map((product) => (
        <Link
          key={product.id}
          to="/$locale/product/$handle"
          params={{ locale, handle: product.handle }}
          className={`${cardSurface} flex-row items-center gap-4`}
        >
          <div className={`${cardMedia} size-24 shrink-0`}>
            {product.featuredImage?.url && (
              <Image
                src={product.featuredImage.url}
                alt={product.featuredImage.altText ?? product.title}
                className="size-full object-contain p-1.5"
                loading="lazy"
                sizes="96px"
              />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 text-base font-bold text-balance text-text-1">
              {product.title}
            </p>
            <Money
              data={product.priceRange.minVariantPrice}
              className="mt-1 text-small text-text-3 tabular-nums"
            />
            <BasePriceLine
              basePrice={cheapestVariantBasePrice(product.variants.nodes)}
            />
            <span className="sr-only">{t("Blog.viewProduct")}</span>
          </div>
          <CardArrow className="mr-2" />
        </Link>
      ))}
    </div>
  );
}
